import { Box, Paper, Skeleton, Typography } from '@offline-mui/material'
import { alpha } from '@offline-mui/material/styles'
import type { ReactNode } from 'react'

export type StatTone = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'

type StatCardProps = {
  label: string
  value: ReactNode
  hint?: ReactNode
  icon?: ReactNode
  tone?: StatTone
  loading?: boolean
}

/**
 * KPI tile: a label, one headline number, and an optional hint line.
 * Numbers use the text token; the icon chip carries the tone colour.
 */
export default function StatCard({
  label,
  value,
  hint,
  icon,
  tone = 'primary',
  loading = false,
}: StatCardProps) {
  return (
    <Paper
      className="fcc-fade-up"
      sx={{
        p: { xs: 2, md: 2.5 },
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        minHeight: 104,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.75 }}
        >
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={96} height={34} />
        ) : (
          <Typography
            component="div"
            sx={{
              fontSize: { xs: 22, md: 26 },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              color: 'text.primary',
            }}
          >
            {value}
          </Typography>
        )}
        {hint ? (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            {hint}
          </Typography>
        ) : null}
      </Box>
      {icon ? (
        <Box
          sx={{
            width: 40,
            height: 40,
            display: { xs: 'none', sm: 'grid' },
            borderRadius: 2.5,
            placeItems: 'center',
            flexShrink: 0,
            bgcolor: (t) => alpha(t.palette[tone].main, 0.12),
            color: (t) => t.palette[tone].dark,
            '& svg': { fontSize: 22 },
          }}
        >
          {icon}
        </Box>
      ) : null}
    </Paper>
  )
}
